import axios from 'axios';
import { triggerUpgradeModal } from './upgradeModalBus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---- Token Management ----

// Decode JWT payload without library (base64url decode)
const decodeJwtPayload = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

// Check if token expires within the given minutes threshold
const isTokenExpiringSoon = (token, thresholdMinutes = 30) => {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true; // treat unknown as expiring
  const expiresAt = payload.exp * 1000; // convert to ms
  const now = Date.now();
  return (expiresAt - now) < (thresholdMinutes * 60 * 1000);
};

// Flag to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise = null;

// Refresh the token using a separate axios instance (bypasses 401 interceptor)
const refreshAxios = axios.create({ baseURL: API_BASE });

const doRefreshToken = async () => {
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;

      const response = await refreshAxios.post('/auth/refresh-token', null, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        return response.data.access_token;
      }
      return null;
    } catch (error) {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Background refresh interval (refresh every 60 minutes as a safety net)
const TOKEN_REFRESH_INTERVAL = 60 * 60 * 1000; // 1 hour
let refreshInterval = null;

const startTokenRefresh = () => {
  if (refreshInterval) return;

  refreshInterval = setInterval(async () => {
    const token = localStorage.getItem('token');
    if (token && isTokenExpiringSoon(token, 60)) {
      await doRefreshToken();
    }
  }, TOKEN_REFRESH_INTERVAL);
};

const stopTokenRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

// Auto-start if user is logged in
if (localStorage.getItem('token')) {
  startTokenRefresh();
}

// ---- Request Interceptor: attach token + proactive refresh ----
apiClient.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('token');

    // Proactively refresh if token expires within 30 minutes
    if (token && isTokenExpiringSoon(token, 30)) {
      const newToken = await doRefreshToken();
      if (newToken) {
        token = newToken;
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---- Response Interceptor: 402 quota → upgrade modal; retry once on 401, then logout ----
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Phase 6: 402 Payment Required → trigger global upgrade modal.
    // Backend (core/quota.py) returns detail = { error:'quota_exceeded', limit_type, current, limit, plan, plan_display_name, upgrade_to, message }
    if (error.response?.status === 402) {
      const detail = error.response.data?.detail;
      if (detail && detail.error === 'quota_exceeded') {
        triggerUpgradeModal(detail);
      } else if (detail && detail.error === 'subscription_expired') {
        // Phase 10: treat expired-plan separately from quota; reuse the modal
        // with a payload the UpgradeModal renders distinctly.
        triggerUpgradeModal({ ...detail, limit_type: 'subscription' });
      }
      return Promise.reject(error);
    }

    // If 401 and we haven't already retried this request
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const newToken = await doRefreshToken();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }

      // Refresh also failed - truly expired, log out
      stopTokenRefresh();
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Only force-redirect to /login if the user is currently on a protected
      // route. If they are on a public page (/, /pricing, /features, /login,
      // /signup, /f/:formId, etc.) we silently clear the stale token and let
      // them stay on the page — no jarring redirect, no toast.
      const PROTECTED_PREFIXES = [
        '/dashboard',
        '/submissions',
        '/forms/',
        '/billing',
        '/profile',
        '/admin',
        '/team',
      ];
      const path = window.location.pathname || '/';
      const onProtected = PROTECTED_PREFIXES.some((p) => path.startsWith(p));
      if (onProtected) {
        window.location.href = '/login?session_expired=true';
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

export const authAPI = {
  signup: (data) => apiClient.post('/auth/signup', data),
  login: (data) => apiClient.post('/auth/login', data).then(res => {
    if (res.data.access_token) {
      startTokenRefresh();
    }
    return res;
  }),
  me: () => apiClient.get('/auth/me'),
  getMe: () => apiClient.get('/auth/me'),
  getUsage: () => apiClient.get('/user/usage'),
  googleSession: (sessionId) => apiClient.post('/auth/google/session', { session_id: sessionId }, { withCredentials: true }).then(res => {
    if (res.data.access_token) {
      startTokenRefresh();
    }
    return res;
  }),
  logout: () => {
    stopTokenRefresh();
    return apiClient.post('/auth/logout', {}, { withCredentials: true });
  },
  refreshToken: () => apiClient.post('/auth/refresh-token'),
};

export const usageAPI = {
  me: () => apiClient.get('/usage/me'),
};

// Phase 10: voucher redemption + admin voucher CRUD
export const vouchersAPI = {
  redeem: (code) => apiClient.post('/vouchers/redeem', { code }),
  // Admin
  adminList: () => apiClient.get('/admin/vouchers'),
  adminCreate: (payload) => apiClient.post('/admin/vouchers', payload),
  adminRevoke: (voucherId) => apiClient.delete(`/admin/vouchers/${voucherId}`),
};

export const formsAPI = {
  create: (data) => apiClient.post('/forms', data),
  getAll: () => apiClient.get('/forms'),
  getOne: (id) => apiClient.get(`/forms/${id}`),
  getPublic: (id) => axios.get(`${API_BASE}/forms/public/${id}`),
  update: (id, data) => apiClient.put(`/forms/${id}`, data),
  delete: (id, keepData = false) => apiClient.delete(`/forms/${id}`, { params: { keep_data: keepData } }),
  getStats: (id) => apiClient.get(`/forms/${id}/stats`),
  duplicate: (id) => apiClient.post(`/forms/${id}/duplicate`),
  submit: (id, data) => axios.post(`${API_BASE}/forms/${id}/submit`, data),
  getSubmissions: (id) => apiClient.get(`/forms/${id}/submissions`),
  getAnalytics: (id, dateRange = '7d') => apiClient.get(`/forms/${id}/analytics`, { params: { date_range: dateRange } }),
  getActivityLogs: (id, limit = 100) => apiClient.get(`/forms/${id}/activity-logs`, { params: { limit } }),
};

export const templatesAPI = {
  getAll: () => axios.get(`${API_BASE}/templates`),
};

export const adminAPI = {
  getStats: () => apiClient.get('/admin/stats'),
  getUsers: () => apiClient.get('/admin/users'),
  getUserDetail: (userId) => apiClient.get(`/admin/users/${userId}`),
  updateUserPlan: (userId, plan) => apiClient.put(`/admin/users/${userId}/plan`, { plan }),
  updateUserStatus: (userId, isActive) => apiClient.put(`/admin/users/${userId}/status`, { is_active: isActive }),
  toggleAdminStatus: (userId) => apiClient.put(`/admin/users/${userId}/admin`),
  deleteUser: (userId) => apiClient.delete(`/admin/users/${userId}`),
  getUserLogs: (userId) => apiClient.get(`/admin/users/${userId}/logs`),
  getEmailSettings: () => apiClient.get('/admin/email-settings'),
  updateEmailSettings: (settings) => apiClient.put('/admin/email-settings', settings),
  testEmailSettings: (toEmail) => apiClient.post('/admin/email-settings/test', { to_email: toEmail }),
  getEmailStatus: () => apiClient.get('/admin/email-settings/status'),
  syncRazorpay: () => apiClient.post('/admin/sync-razorpay'),
  getPayments: (skip = 0, limit = 50) => apiClient.get(`/admin/payments?skip=${skip}&limit=${limit}`),
};

export const integrationsAPI = {
  getAvailable: (formId) => apiClient.get(`/integrations/available${formId ? `?form_id=${formId}` : ''}`),
  getFormIntegrations: (formId) => apiClient.get(`/forms/${formId}/integrations`),
  updateFormIntegration: (formId, integrationType, config) =>
    apiClient.put(`/forms/${formId}/integrations/${integrationType}`, config),
  getFormLogs: (formId) => apiClient.get(`/forms/${formId}/integration-logs`),
  startGoogleSheetsOAuth: (formId) =>
    apiClient.get(`/integrations/oauth/google-sheets/start${formId ? `?form_id=${formId}` : ''}`),
  startGoogleDriveOAuth: (formId) =>
    apiClient.get(`/integrations/oauth/google-drive/start${formId ? `?form_id=${formId}` : ''}`),
  startSlackOAuth: (formId) =>
    apiClient.get(`/integrations/oauth/slack/start${formId ? `?form_id=${formId}` : ''}`),
  startGoogleCalendarOAuth: (formId) =>
    apiClient.get(`/integrations/oauth/google-calendar/start${formId ? `?form_id=${formId}` : ''}`),
  startZoomOAuth: (formId) =>
    apiClient.get(`/integrations/oauth/zoom/start${formId ? `?form_id=${formId}` : ''}`),
  startDropboxOAuth: (formId) =>
    apiClient.get(`/integrations/oauth/dropbox/start${formId ? `?form_id=${formId}` : ''}`),
  listSlackChannels: () => apiClient.get('/integrations/slack/channels'),
  test: (integrationType) => apiClient.post(`/integrations/${integrationType}/test`),
  disconnect: (integrationType) => apiClient.delete(`/integrations/${integrationType}/disconnect`),
};
